import { ActivityType } from './index'

// Guest details for booking (weight required for flight activities)
export interface GuestDetail {
  name: string
  weight?: number // kg - required for paramotor/balloon
}

// Transport option for boat cruise
export interface TransportOption {
  enabled: boolean
  price: number // Fixed price for up to 8 passengers
  maxPassengers: number
}

// Individual cart item representing one activity booking
export interface CartItem {
  id: string // unique ID for cart item (uuid)
  activityId: string
  activitySlug: string
  activityTitle: string
  activityType: ActivityType
  activityImage?: string // first image for display

  // Pricing
  pricePerPerson: number
  seasonName?: string

  // Booking details
  selectedDate: string // YYYY-MM-DD format
  partySize: number
  guestDetails: GuestDetail[] // Array for all guests including lead booker
  flightTime?: 'sunrise' | 'sunset' // For paramotor/balloon
  tourType?: 'half_day' | 'full_day' | '2_day' | '6_day' // For enduro tours — drives the compta product variant
  transportOption?: TransportOption // For boat cruise transport from Vang Vieng
  notes?: string
  customerPhone?: string // WhatsApp number from booking form
  accommodation?: string // Hotel/guesthouse name

  // Computed
  subtotal: number // pricePerPerson * partySize

  // Timestamp for ordering
  addedAt: number
}

// Customer information (collected at checkout)
export interface CustomerInfo {
  name: string
  email: string
  phone?: string
  accommodation?: string
}

// Persisted cart state in localStorage
export interface PersistedCartState {
  items: CartItem[]
  customerInfo: CustomerInfo | null
  version: number // For future migrations
  lastUpdated: number // Timestamp
}

// Checkout step states
export type CheckoutStep = 'cart' | 'checkout' | 'payment'

// Cart context interface
export interface CartContextType {
  // State
  items: CartItem[]
  customerInfo: CustomerInfo | null
  isOpen: boolean
  checkoutStep: CheckoutStep

  // Cart actions
  addItem: (item: Omit<CartItem, 'id' | 'addedAt' | 'subtotal'>) => void
  removeItem: (id: string) => void
  updateItem: (id: string, updates: Partial<CartItem>) => void
  clearCart: () => void

  // Customer info
  setCustomerInfo: (info: CustomerInfo) => void
  clearCustomerInfo: () => void

  // UI actions
  openCart: () => void
  closeCart: () => void
  toggleCart: () => void
  setCheckoutStep: (step: CheckoutStep) => void

  // Computed
  getItemCount: () => number
  getTotal: () => number
  hasFlightActivities: () => boolean
  isEmpty: () => boolean
}

// Activities that require weight information
export const ACTIVITIES_REQUIRING_WEIGHT: ActivityType[] = [
  'paramotor',
  'hot_air_balloon',
  'package',
]

// Activities that have flight time options (sunrise/sunset)
export const ACTIVITIES_WITH_FLIGHT_TIME: ActivityType[] = [
  'paramotor',
  'hot_air_balloon',
  'package',
]

// Helper to check if activity requires weight
export const requiresWeight = (activityType: ActivityType): boolean => {
  return ACTIVITIES_REQUIRING_WEIGHT.includes(activityType)
}

// Helper to check if activity has flight time options
export const hasFlightTimeOptions = (activityType: ActivityType): boolean => {
  return ACTIVITIES_WITH_FLIGHT_TIME.includes(activityType)
}

// Activity type emoji mapping for display
export const ACTIVITY_EMOJI: Record<ActivityType, string> = {
  paramotor: '🪂',
  hot_air_balloon: '🎈',
  boat_cruise: '🚤',
  tuktuk: '🛺',
  enduro: '🏍️',
  package: '📦',
  trekking: '🥾',
  rock_climbing: '🧗',
}

// Get emoji for activity type
export const getActivityEmoji = (activityType: ActivityType): string => {
  return ACTIVITY_EMOJI[activityType] || '🎯'
}
